"use client";
import "./funds_performance.scss";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Container } from "@/elements/container/container";
import { Stack } from "@/elements/stack/stack";
import { Text } from "@/elements/text/text";
import { i18n } from "@/model/i18n";
import { type ReactElement } from "react";
import { Formatter } from "@/model/formatter";
import { useFunds } from "@/contexts/funds-context";
import { PerformanceValue } from "@/components/performance_value/performance_value";

const formatter = new Formatter({ locale: "de" });

export function FundsPerformance(): ReactElement | null {
  const { selectedTranche } = useFunds();

  const data = selectedTranche?.dailyData.map((entry) => ({
    name: formatter.numeralDate(entry.calendarDate),
    performance: entry.performancePerc ?? 0,
  }));

  return (
    <section className="funds-performance" aria-label="funds-performance">
      <Container>
        <Stack size={40} tabletSize={80}>
          <Text type="h2-xbold" color="primary-on-default" htmlElement="h2">
            {i18n.t("pages.funds.performance.title")}
          </Text>
          <Stack size={60}>
            <section
              aria-label="performance-chart"
              className="funds-performance__chart"
            >
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data}>
                  <defs>
                    <linearGradient
                      id="colorPerformance"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="var(--accent-tinted)"
                        stopOpacity={0.6}
                      />
                      <stop
                        offset="96%"
                        stopColor="rgba(215, 255, 217, 0.00)"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="name"
                    stroke="var(--lines-mid-contrast)"
                    strokeWidth={1}
                    tickLine={false}
                    tick={CustomizedXAxisTick}
                  />
                  <YAxis
                    stroke="var(--lines-mid-contrast)"
                    strokeWidth={1}
                    tickLine={false}
                    tick={CustomizedYAxisTick}
                    width={35}
                  />
                  <CartesianGrid
                    stroke="var(--lines-mid-contrast)"
                    strokeWidth={1}
                  />
                  <Tooltip
                    content={CustomTooltip}
                    cursor={{
                      strokeWidth: 1,
                      stroke: "var(--primary-on-default)",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="performance"
                    stroke="var(--accent-tinted)"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorPerformance)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </section>
            <Stack size={40} tabletSize={60}>
              <section
                className="funds-performance__section"
                aria-label="performance-cumulative"
              >
                <Text
                  type="body-xbold"
                  color="darker-pairings-primary"
                  htmlElement="h3"
                >
                  {i18n.t("pages.funds.performance.performance_cumulative")}
                </Text>
                <Stack size={40}>
                  <ul className="funds-performance__cumulative-list">
                    <li className="funds-performance__cumulative-item">
                      <Text
                        type="micro"
                        color="neutral-on-surface-lightest"
                        htmlElement="span"
                      >
                        {i18n.t("pages.funds.performance.year")}
                      </Text>
                      <div className="funds-performance__cumulative-value">
                        <PerformanceValue
                          value={selectedTranche?.performance1yPerc}
                        />
                      </div>
                    </li>
                    <li className="funds-performance__cumulative-item">
                      <Text
                        type="micro"
                        color="neutral-on-surface-lightest"
                        htmlElement="span"
                      >
                        {i18n.t(
                          "pages.funds.performance.performance_current_year",
                        )}
                      </Text>
                      <div className="funds-performance__cumulative-value">
                        <PerformanceValue
                          value={
                            selectedTranche?.dailyData.slice(-1)[0]
                              ?.performanceYtdPerc
                          }
                        />
                      </div>
                    </li>
                    <li className="funds-performance__cumulative-item">
                      <Text
                        type="micro"
                        color="neutral-on-surface-lightest"
                        htmlElement="span"
                      >
                        {i18n.t(
                          "pages.funds.performance.performance_since_inception",
                          {
                            date:
                              selectedTranche?.launchedAt &&
                              formatter.numeralDate(
                                selectedTranche?.launchedAt,
                              ),
                          },
                        )}
                      </Text>
                      <div className="funds-performance__cumulative-value">
                        <PerformanceValue
                          value={selectedTranche?.performanceTotalPerc}
                        />
                      </div>
                    </li>
                  </ul>
                  <Text
                    type="caption"
                    color="neutral-on-surface"
                    htmlElement="small"
                  >
                    {i18n.t("pages.funds.performance.disclaimer")}
                    <br />
                    {i18n.t("pages.funds.performance.performance_note")}
                  </Text>
                </Stack>
              </section>
              <section
                className="funds-performance__section"
                aria-label="performance-annualised"
              >
                <Text
                  type="body-xbold"
                  color="darker-pairings-primary"
                  htmlElement="h3"
                >
                  {i18n.t("pages.funds.performance.annualised_performance")}
                </Text>
                <ul className="funds-performance__cumulative-list">
                  <li className="funds-performance__cumulative-item">
                    <Text
                      type="micro"
                      color="neutral-on-surface-lightest"
                      htmlElement="span"
                    >
                      {i18n.t("pages.funds.performance.years", { years: 3 })}
                    </Text>
                    <div className="funds-performance__cumulative-value">
                      <PerformanceValue
                        value={selectedTranche?.performanceAnnualized3yPerc}
                      />
                    </div>
                  </li>
                  <li className="funds-performance__cumulative-item">
                    <Text
                      type="micro"
                      color="neutral-on-surface-lightest"
                      htmlElement="span"
                    >
                      {i18n.t("pages.funds.performance.years", { years: 5 })}
                    </Text>
                    <div className="funds-performance__cumulative-value">
                      <PerformanceValue
                        value={selectedTranche?.performanceAnnualized5yPerc}
                      />
                    </div>
                  </li>
                  <li className="funds-performance__cumulative-item">
                    <Text
                      type="micro"
                      color="neutral-on-surface-lightest"
                      htmlElement="span"
                    >
                      {i18n.t("pages.funds.performance.years", {
                        years: 10,
                      })}
                    </Text>
                    <div className="funds-performance__cumulative-value">
                      <PerformanceValue
                        value={selectedTranche?.performanceAnnualized10yPerc}
                      />
                    </div>
                  </li>
                </ul>
              </section>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </section>
  );
}

const CustomizedYAxisTick = ({
  x,
  y,
  payload,
}: {
  x: number;
  y: number;
  payload: { value: string };
}): ReactElement<SVGElement> => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        textAnchor="end"
        fill="var(--neutral-on-surface-lightest)"
        className="text text--chart-label-axis"
      >
        {formatter.roundPercentage(Number(payload.value))}
      </text>
    </g>
  );
};

const CustomizedXAxisTick = ({
  x,
  y,
  payload,
}: {
  x: number;
  y: number;
  payload: { value: string };
}): ReactElement<SVGElement> => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={12}
        textAnchor="middle"
        fill="var(--neutral-on-surface-lightest)"
        className="text text--chart-label-axis"
      >
        {payload.value}
      </text>
    </g>
  );
};

const CustomTooltip = ({
  active,
  payload,
}: {
  active: boolean;
  payload: Array<{ value: number }>;
}): ReactElement | null => {
  const isVisible = active && payload && payload.length;
  const value = formatter.percentage(payload[0]?.value ?? 0);

  return (
    <div
      className="funds-performance__tooltip"
      style={{ visibility: isVisible ? "visible" : "hidden" }}
    >
      {isVisible && (
        <Text type="snippet-body" color="primary-on-default">
          {value}
        </Text>
      )}
    </div>
  );
};
